import React, { useState, useEffect, useMemo } from "react";
import { transferTargetTypeFor } from "../../../Services/resourceAccount";
import {
  Button,
  Dropdown,
  Input,
  Option,
  Text,
  makeStyles,
} from "@fluentui/react-components";
import {
  Delete20Regular,
  Add20Regular,
} from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";

import IvrComponent from "../Routing/IvrComponent";
import PromptContainer from "../Routing/PromptContainer";
import RegexpComponent from "../Routing/RegexpComponent";
import TimeComponent from "../Routing/TimeComponent";
import DaysComponent from "../Routing/DaysComponent";
import CheckVariableComponent from "../Routing/CheckVariableComponent";
import QueueSelectorComponent from "../../GenericComponent/QueueSelectorComponent";
import SearchResourceInGraph from "../../GenericComponent/SearchResourceInGraph";
import { ConfirmAlert } from "../../GenericComponent/ConfirmAlert";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
    height: "100%",
  },
  tabContent: {
    flex: 1,
    overflowY: "auto",
    padding: "0.5rem",
  },
  addRow: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    padding: "0.5rem",
    backgroundColor: "#f9f9f9",
    borderRadius: "8px",
  },
  editPanel: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
  },
  editingTitle: {
    fontWeight: 600,
    fontSize: "1.2rem",
    marginBottom: "0.5rem",
    color: "#333",
  },
});

const ROUTING_TYPES = [
  "Queue",
  "Transfer",
  "Disconnect",
  "IVR",
  "Prompt",
  "Regexp",
  "Time",
  "Check days",
  "Check variable",
];

export default function RoutingEditPanel({ callFlowData, setCallFlowData, selectedRouting = null, ACTIVE_TENANT, onSave }) {
  const { t } = useTranslation();
  const classes = useStyles();

  const [routingToDelete, setRoutingToDelete] = useState(null);
  const [selectedRoutingName, setSelectedRoutingName] = useState(() => {
    if (selectedRouting && callFlowData.routing?.[selectedRouting]) return selectedRouting;
    const routingKeys = Object.keys(callFlowData.routing || {});
    if (routingKeys.includes("0")) return "0";
    return routingKeys.length > 0 ? routingKeys[0] : null;
  });

  const routingMap = useMemo(() => callFlowData.routing || {}, [callFlowData.routing]);

  // Si un routing est sélectionné depuis l'extérieur
  useEffect(() => {
    if (selectedRouting && routingMap[selectedRouting]) {
      setSelectedRoutingName(selectedRouting);
    }
  }, [selectedRouting, routingMap]);

  const updateRouting = (routingName, updater) => {
    setCallFlowData((prev) => {
      const routing = { ...(prev.routing || {}) };
      const current = routing[routingName] || {};
      routing[routingName] = typeof updater === "function" ? updater(current) : updater;
      // Ajouter un timestamp pour forcer la détection de changement et la régénération du diagramme
      return { ...prev, routing, _updateTimestamp: Date.now() };
    });
  };

  const handleRoutingTypeChange = (routingName, value) => {
    let type = value.toLowerCase();
    if (type === "check variable") type = "check_variable";
    if (type === "check days") type = "check_days";

    updateRouting(routingName, (node) => {
      const updated = { ...node, type };
      
      // Initialiser audio pour IVR et Prompt
      if (type === "ivr" || type === "prompt") {
        updated.audio = { ...(updated.audio || {}), type: "text2speech" };
      }
      
      // Nettoyer les propriétés selon le type
      if (type === "prompt") {
        // Pour prompt, garder seulement: enabled, displayName, type, audio, next, promptDisplayName
        delete updated.options;
        delete updated.regExp;
        delete updated.onMatched;
        delete updated.onUnMatched;
        delete updated.daysList;
        delete updated.formula;
        delete updated.queueId;
        delete updated.queueDisplayName;
        delete updated.targetId;
        delete updated.targetType;
        delete updated.userDisplayName;
        delete updated.routingName;
      } else if (type === "ivr") {
        // Pour IVR, garder: enabled, displayName, type, audio, options, expectedDigitNumber, lastTone, timeout
        delete updated.next;
        delete updated.onMatched;
        delete updated.onUnMatched;
        delete updated.regExp;
        delete updated.daysList;
        delete updated.formula;
        delete updated.promptDisplayName;
      } else if (type === "queue") {
        // Pour queue, garder: enabled, displayName, type, queueId, queueDisplayName
        delete updated.options;
        delete updated.next;
        delete updated.onMatched;
        delete updated.onUnMatched;
        delete updated.regExp;
        delete updated.daysList;
        delete updated.formula;
        delete updated.audio;
        delete updated.promptDisplayName;
        delete updated.expectedDigitNumber;
        delete updated.lastTone;
        delete updated.timeout;
      } else if (type === "regexp" || type === "check_variable" || type === "time" || type === "check_days") {
        // Pour regexp/check_variable/time/check_days, garder: enabled, displayName, type, onMatched, onUnMatched, regExp/formula/daysList
        delete updated.options;
        delete updated.next;
        delete updated.audio;
        delete updated.promptDisplayName;
        delete updated.expectedDigitNumber;
        delete updated.lastTone;
        delete updated.timeout;
        delete updated.queueId;
        delete updated.queueDisplayName;
      } else if (type === "disconnect") {
        // Pour disconnect, garder seulement: enabled, displayName, type
        delete updated.options;
        delete updated.next;
        delete updated.onMatched;
        delete updated.onUnMatched;
        delete updated.regExp;
        delete updated.daysList;
        delete updated.formula;
        delete updated.audio;
        delete updated.promptDisplayName;
        delete updated.queueId;
        delete updated.queueDisplayName;
        delete updated.targetId;
        delete updated.targetType;
        delete updated.userDisplayName;
        delete updated.routingName;
      } else if (type === "transfert" || type === "transfer") {
        // Pour transfer, garder: enabled, displayName, type, targetId, targetType, userDisplayName
        delete updated.options;
        delete updated.next;
        delete updated.onMatched;
        delete updated.onUnMatched;
        delete updated.regExp;
        delete updated.daysList;
        delete updated.formula;
        delete updated.audio;
        delete updated.promptDisplayName;
        delete updated.queueId;
        delete updated.queueDisplayName;
      }
      
      return updated;
    });
  };

  const handleFieldChange = (routingName, field, value) => {
    updateRouting(routingName, (node) => {
      const updated = { ...node };
      const parts = field.split(".");
      let obj = updated;
      for (let i = 0; i < parts.length - 1; i++) {
        if (!obj[parts[i]]) obj[parts[i]] = {};
        obj = obj[parts[i]];
      }
      obj[parts[parts.length - 1]] = value;
      return updated;
    });
  };

  const handleAddRouting = (name) => {
    if (!name || routingMap[name]) return;

    const newRouting = {
      enabled: true,
      displayName: "",
      routingName: name,
      type: "queue",
      queueDisplayName: "",
      queueId: "",
    };

    setCallFlowData((prev) => ({
      ...prev,
      routing: { ...(prev.routing || {}), [name]: newRouting },
    }));

    setSelectedRoutingName(name);
  };

  const confirmDeleteRouting = (name) => {
    // Protéger le routing "0" (routing par défaut)
    if (name === '0') {
      return;
    }
    setRoutingToDelete(name);
  };

  const handleDeleteRouting = () => {
    const name = routingToDelete;
    
    // Double vérification pour protéger le routing "0"
    if (name === '0') {
      setRoutingToDelete(null);
      return;
    }
    
    setCallFlowData((prev) => {
      if (!prev || !prev.routing) return prev;
      
      const deletedRouting = prev.routing[name];
      const deletedRoutingName = deletedRouting?.routingName || name;
      
      
      // Supprimer le routing
      const { [name]: removed, ...remainingRoutings } = prev.routing;
      
      // Nettoyer toutes les références à ce routing dans les autres routings
      Object.keys(remainingRoutings).forEach(rKey => {
        const routing = remainingRoutings[rKey];
        
        // Nettoyer les options IVR
        if ((routing.type === 'ivr' || routing.type === 'IVR') && routing.options) {
          const updatedOptions = {};
          let optionsChanged = false;
          
          Object.entries(routing.options).forEach(([dtmf, option]) => {
            // Si l'option pointe vers le routing supprimé par routingName
            if (option.type === 'routing' && 
                (option.routingName === name || option.routingName === deletedRoutingName)) {
              optionsChanged = true;
            } 
            // Vérifier aussi les options directes (queue, transfer, disconnect)
            else if (deletedRouting?.type === 'queue' && option.type === 'queue' && 
                     option.queueId === deletedRouting.queueId) {
              optionsChanged = true;
            } else if (deletedRouting?.type === 'transfer' && option.type === 'transfer' &&
                       option.targetType === deletedRouting.targetType &&
                       option.targetId === deletedRouting.targetId) {
              optionsChanged = true;
            } else if (deletedRouting?.type === 'disconnect' && option.type === 'disconnect') {
              optionsChanged = true;
            } else {
              updatedOptions[dtmf] = option;
            }
          });
          
          if (optionsChanged) {
            remainingRoutings[rKey] = {
              ...routing,
              options: updatedOptions
            };
          }
        }
        
        // Nettoyer le next des Prompts
        if (routing.type === 'prompt' && routing.next) {
          if (routing.next.type === 'routing' && 
              (routing.next.routingName === name || routing.next.routingName === deletedRoutingName)) {
            const { next, ...restRouting } = routing;
            remainingRoutings[rKey] = restRouting;
          } else if (deletedRouting?.type === 'disconnect' && routing.next.type === 'disconnect') {
            const { next, ...restRouting } = routing;
            remainingRoutings[rKey] = restRouting;
          }
        }
        
        // Nettoyer onMatched/onUnMatched des routings conditionnels
        if (routing.type === 'check_variable' || routing.type === 'time' || routing.type === 'check_days' || routing.type === 'regexp') {
          let updated = false;
          const newRouting = { ...routing };
          
          if (routing.onMatched?.type === 'routing' && 
              (routing.onMatched.routingName === name || routing.onMatched.routingName === deletedRoutingName)) {
            delete newRouting.onMatched;
            updated = true;
          } else if (deletedRouting?.type === 'disconnect' && routing.onMatched?.type === 'disconnect') {
            delete newRouting.onMatched;
            updated = true;
          }
          
          if (routing.onUnMatched?.type === 'routing' && 
              (routing.onUnMatched.routingName === name || routing.onUnMatched.routingName === deletedRoutingName)) {
            delete newRouting.onUnMatched;
            updated = true;
          } else if (deletedRouting?.type === 'disconnect' && routing.onUnMatched?.type === 'disconnect') {
            delete newRouting.onUnMatched;
            updated = true;
          }
          
          if (updated) {
            remainingRoutings[rKey] = newRouting;
          }
        }
      });
      
      const updatedCallFlow = { ...prev, routing: remainingRoutings };
      
      // Sauvegarder immédiatement après suppression
      if (onSave) {
        onSave('routing', updatedCallFlow);
      }
      
      return updatedCallFlow;
    });

    if (selectedRoutingName === name) setSelectedRoutingName(null);
    setRoutingToDelete(null);
  };

  const setOnMatched = (routingName, value) => {
    updateRouting(routingName, (node) => ({ ...node, onMatched: value }));
  };

  const setOnUnMatched = (routingName, value) => {
    updateRouting(routingName, (node) => ({ ...node, onUnMatched: value }));
  };

  const getOtherRoutings = (currentRoutingName) => {
    return Object.keys(routingMap)
      .filter((k) => k !== currentRoutingName)
      .reduce((obj, k) => ({ ...obj, [k]: routingMap[k] }), {});
  };

  const displayRoutingName = (name) => {
    return name === "0" ? "Start" : routingMap[name]?.displayName || name;
  };

  const renderListTab = () => {
    const routingNames = Object.keys(routingMap).map((rName) => ({
      name: rName,
      displayName: rName === "0" ? "Start" : routingMap[rName].displayName || rName,
      type: routingMap[rName].type || "queue",
    }));

    return (
      <div className={classes.listPanel}>
        {/* List of routings */}
        {routingNames.map(({ name, displayName, type }) => (
          <div
            key={name}
            className={`${classes.routingItem} ${
              selectedRoutingName === name ? classes.selectedRouting : ""
            }`}
            onClick={() => {
              setSelectedRoutingName(name);
            }}
          >
            <div className={classes.routingInfo}>
              <div className={classes.routingName}>{displayName}</div>
              <div className={classes.routingType}>
                {t(`${type}`, type)}
              </div>
            </div>
            {name !== "0" && (
              <Button
                appearance="subtle"
                icon={<Delete20Regular />}
                onClick={(e) => {
                  e.stopPropagation();
                  confirmDeleteRouting(name);
                }}
              />
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderEditTab = () => {
    if (!selectedRoutingName || !routingMap[selectedRoutingName]) {
      return <Text>Sélectionnez un routing à éditer</Text>;
    }

    const routing = routingMap[selectedRoutingName];
    const rawType = routing.type || "queue";
    const displayType = rawType === "check_variable" 
      ? t("checkVariable") 
      : rawType === "check_days"
      ? t("checkDays")
      : rawType === "regexp"
      ? t("regexp")
      : rawType === "time"
      ? t("time")
      : rawType === "prompt"
      ? t("prompt")
      : rawType === "ivr"
      ? "IVR"
      : t(`${rawType}`, rawType);

    return (
      <div className={classes.editPanel}>
        {/* Title */}
        <div className={classes.editingTitle}>
          {displayRoutingName(selectedRoutingName)} - Editing
        </div>

        {/* Routing Name - Pour tous les types */}
        <label style={{ fontWeight: 600, marginTop: '1rem' }}>{t("routingName")}</label>
        <Input
          value={selectedRoutingName === '0' ? 'Start' : (routing.displayName || routing.routingName || '')}
          onChange={(_, data) => 
            handleFieldChange(selectedRoutingName, 'displayName', data.value)
          }
          placeholder={t("enterRoutingName")}
          disabled={selectedRoutingName === '0'}
        />

        <label style={{ fontWeight: 600, marginTop: '1rem' }}>{t("routingType")}</label>
        <Dropdown
          placeholder={t("selectRoutingType")}
          value={displayType}
          onOptionSelect={(_, data) =>
            handleRoutingTypeChange(selectedRoutingName, data.optionValue || data.optionText)
          }
        >
          {ROUTING_TYPES.map((tKey) => (
            <Option key={tKey} value={tKey}>
              {t(`${tKey.toLowerCase().replace(" ", "")}`, tKey)}
            </Option>
          ))}
        </Dropdown>

        {/* Conditional rendering by type */}
        {(() => {
          switch (rawType) {
            case "queue":
              return (
                <QueueSelectorComponent
                  displayName={routing.queueDisplayName || ""}
                  getData={(queue) => {
                    handleFieldChange(selectedRoutingName, "queueDisplayName", queue.displayName);
                    handleFieldChange(selectedRoutingName, "queueId", queue.id);
                    handleFieldChange(selectedRoutingName, "type", "queue");
                  }}
                />
              );
            case "transfer":
              return (
                <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                  <div>
                    <Text weight="semibold">Target Type</Text>
                    <Dropdown
                      placeholder="Select target type"
                      value={routing.targetType || "user"}
                      onOptionSelect={(_, data) => handleFieldChange(selectedRoutingName, "targetType", data.optionValue)}
                    >
                      <Option value="user">User</Option>
                      <Option value="phone">Phone Number</Option>
                      {/* « app » n'est pas proposé : le type d'une cible Teams est un FAIT que
                          Graph nous apprend, pas un choix. Il est déduit à la sélection
                          ci-dessous. L'option n'apparaît que pour rouvrir un flux déjà
                          configuré sans que le sélecteur paraisse vide. */}
                      {routing.targetType === "app" && (
                        <Option value="app">Resource account</Option>
                      )}
                    </Dropdown>
                  </div>
                  
                  {routing.targetType === "phone" && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <Text weight="semibold">Phone Number</Text>
                      <Input
                        placeholder="Enter phone number"
                        value={routing.targetId || ""}
                        onChange={(e) => handleFieldChange(selectedRoutingName, "targetId", e.target.value)}
                        style={{ width: '100%' }}
                      />
                    </div>
                  )}
                  
                  {(routing.targetType === "user" || routing.targetType === "app") && (
                    <div>
                      <Text weight="semibold">Select User</Text>
                      <SearchResourceInGraph
                        activeTab="users"
                        label=""
                        displayName={routing.displayName || routing.userDisplayName || ""}
                        onSelectUser={(user) => {
                          handleFieldChange(selectedRoutingName, "targetId", user.id);
                          handleFieldChange(selectedRoutingName, "displayName", user.displayName);
                          handleFieldChange(selectedRoutingName, "userDisplayName", user.displayName);
                          // Un compte ressource Teams (file, standard automatique) s'adresse avec
                          // un identifiant d'APPLICATION. L'enregistrer comme « user » produit un
                          // identifiant utilisateur inexistant : ACS accepte, le transfert ne
                          // sonne jamais, et rien ne le signale.
                          handleFieldChange(selectedRoutingName, "targetType", transferTargetTypeFor(user));
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            case "disconnect":
              return (
                <div>
                  <Text>This routing will disconnect the call.</Text>
                  <Text size={200} style={{ color: "#666", marginTop: "0.5rem" }}>
                    No additional configuration required.
                  </Text>
                </div>
              );
            case "ivr":
              return (
                <IvrComponent
                  callFlowData={callFlowData}
                  data={routing}
                  routingName={selectedRoutingName}
                  ACTIVE_TENANT={ACTIVE_TENANT}
                  onChange={handleFieldChange}
                  onAddOption={(key, option) =>
                    updateRouting(selectedRoutingName, (node) => ({
                      ...node,
                      options: { ...(node.options || {}), [key]: option },
                    }))
                  }
                  onRemoveOption={(key) =>
                    updateRouting(selectedRoutingName, (node) => {
                      const opts = { ...(node.options || {}) };
                      delete opts[key];
                      return { ...node, options: opts };
                    })
                  }
                  allRoutings={getOtherRoutings(selectedRoutingName)}
                  permissions={{ routing: { canUpdate: true, canCreate: true, canRead: true } }}
                  isEditMode={true}
                />
              );
            case "prompt":
              return (
                <PromptContainer
                  callFlowData={callFlowData}
                  data={routing}
                  routingName={selectedRoutingName}
                  onChange={handleFieldChange}
                  allRoutings={routingMap}
                  ACTIVE_TENANT={ACTIVE_TENANT}
                  permissions={{ routing: { canUpdate: true, canCreate: true, canRead: true } }}
                  isEditMode={true}
                />
              );
            case "regexp":
              return (
                <RegexpComponent
                  data={routing}
                  routingName={selectedRoutingName}
                  onChange={handleFieldChange}
                  setOnMatched={(val) => setOnMatched(selectedRoutingName, val)}
                  setOnUnMatched={(val) => setOnUnMatched(selectedRoutingName, val)}
                  routingList={routingMap}
                  permissions={{ routing: { canUpdate: true, canCreate: true, canRead: true } }}
                  isEditMode={true}
                />
              );
            case "time":
              return (
                <TimeComponent
                  routingList={routingMap}
                  data={routing}
                  routingName={selectedRoutingName}
                  onChange={handleFieldChange}
                  setOnMatched={(val) => setOnMatched(selectedRoutingName, val)}
                  setOnUnMatched={(val) => setOnUnMatched(selectedRoutingName, val)}
                  permissions={{ routing: { canUpdate: true, canCreate: true, canRead: true } }}
                  isEditMode={true}
                />
              );
            case "check_days":
              return (
                <DaysComponent
                  routingList={routingMap}
                  data={routing}
                  routingName={selectedRoutingName}
                  onChange={handleFieldChange}
                  setOnMatched={(val) => setOnMatched(selectedRoutingName, val)}
                  setOnUnMatched={(val) => setOnUnMatched(selectedRoutingName, val)}
                  permissions={{ routing: { canUpdate: true, canCreate: true, canRead: true } }}
                  isEditMode={true}
                />
              );
            case "check_variable":
              return (
                <CheckVariableComponent
                  data={routing}
                  routingName={selectedRoutingName}
                  onChange={handleFieldChange}
                  setOnMatched={(val) => setOnMatched(selectedRoutingName, val)}
                  setOnUnMatched={(val) => setOnUnMatched(selectedRoutingName, val)}
                  routingList={routingMap}
                  permissions={{ routing: { canUpdate: true, canCreate: true, canRead: true } }}
                  isEditMode={true}
                />
              );
            default:
              return <Text>Type de routing non supporté</Text>;
          }
        })()}
      </div>
    );
  };

  return (
    <div className={classes.container}>
      {/* Edit Content */}
      <div className={classes.tabContent}>
        {renderEditTab()}
      </div>

      {/* Delete Confirmation Dialog */}
      {routingToDelete && (
        <ConfirmAlert
          open={true}
          name={displayRoutingName(routingToDelete)}
          onConfirm={handleDeleteRouting}
          onCancel={() => {
            setRoutingToDelete(null);
          }}
        />
      )}
    </div>
  );
}
